import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  AROrgeCampaign,
  AROrgeCampaignConfiguration,
  AROrgeOrder,
  AROrgeOrderItem,
  AROrgeParcel,
} from '../models/agrorendement-orge.model';
import { Page } from '../models/page.model';
import { OrderItemDeletionSummary, normalizeDeletionSummary } from '../models/order-item-deletion.model';
import { Parcel } from '../models/parcel.model';
import { HttpWanaka } from '../utils/http.wanaka';
import { tryJson } from '../utils/utils';
import { AROrderFilter, ARResultService, OrderSummary, fillOrderFilter } from './agrorendement-common.service';

export class AROrgeParcelFilter {
  prefixParcel: string;
  prefixExploitation: string;
  farmId: number | string;
  campaignId: number | number[];
  organisationId: number | string;
  orderStatus: boolean;
  completionStatus: boolean;
  prefixIdParcel: number;
  specy: number | string;
  advisorId: number | string;
}

@Injectable({ providedIn: 'root' })
export class AROrgeService extends ARResultService<AROrgeOrderItem, AROrgeCampaign> {
  // utilisé par ARResultService
  protected toolUrl = '/tools/agrorendement/orge';

  constructor(
    protected http: HttpClient,
    protected httpWanaka: HttpWanaka,
  ) {
    super(http, httpWanaka);
  }

  canPerfomLifecycleUpdates(): Observable<boolean> {
    const url = `${this.toolUrl}/results/canManage`;
    return this.http.get<any>(url).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }
  protected handleError(error: any) {
    // FIXME
    console.error('An error occurred', error);
    return throwError(() => error.message || error);
  }

  getPageCampaigns(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: string,
  ): Observable<Page<AROrgeCampaign>> {
    let url = this.toolUrl + '/campaigns?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    // filter pas supporté pour l'instant
    if (filter) {
      url += '&year=' + filter;
    }
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        data.content.forEach((element: AROrgeCampaign) => {
          this.handleCampaignDates(element);
          if (element.configuration) {
            this.handleConfigurationDates(element.configuration);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaigns(): Observable<AROrgeCampaign[]> {
    return this.http.get<any>(this.toolUrl + '/campaigns/all?configurationStatus=true').pipe(
      map((response) => {
        const data: AROrgeCampaign[] = response.data;
        data.forEach((element) => {
          this.handleCampaignDates(element);
          if (element.configuration) {
            this.handleConfigurationDates(element.configuration);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaign(id: number): Observable<AROrgeCampaign> {
    return this.http.get<any>(this.toolUrl + '/campaigns/' + id).pipe(
      map((response) => {
        const data: AROrgeCampaign = response.data;
        this.handleCampaignDates(data);
        if (data.configuration) {
          this.handleConfigurationDates(data.configuration);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  updateParcelsBatch(parcelIds: number[], newDate: any, passage: string, isSuspended: any): Observable<any> {
    const payload: any = { parcelIds, passage };

    if (newDate) {
      payload.newDate = newDate;
    }

    if (isSuspended !== undefined && isSuspended !== null) {
      payload.isSuspended = isSuspended;
    }

    return this.http.put<any>(`${this.toolUrl}/orderitems/update-batch`, payload);
  }

  getCampaignConfigurations(): Observable<AROrgeCampaignConfiguration[]> {
    return this.http.get<any>(this.toolUrl + '/configs/all').pipe(
      map((response) => {
        const data: AROrgeCampaignConfiguration[] = response.data;
        data.forEach((element: AROrgeCampaignConfiguration) => {
          this.handleConfigurationDates(element);
          if (element.campaign) {
            this.handleCampaignDates(element.campaign);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaignConfiguration(id: number): Observable<AROrgeCampaignConfiguration> {
    return this.http.get<any>(this.toolUrl + '/configs/' + id).pipe(
      map((response) => {
        const data: AROrgeCampaignConfiguration = this.handleConfigurationDates(response.data);
        if (data.campaign) {
          this.handleCampaignDates(data.campaign);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  saveCampaignConfiguration(config: AROrgeCampaignConfiguration): Observable<AROrgeCampaignConfiguration> {
    if (config.id) {
      return this.putCampaignConfiguration(config);
    }
    return this.postCampaignConfiguration(config);
  }

  deleteCampaignConfiguration(config: AROrgeCampaignConfiguration) {
    const url = `${this.toolUrl}/configs/${config.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  // Add new campaign
  private postCampaignConfiguration(config: AROrgeCampaignConfiguration): Observable<AROrgeCampaignConfiguration> {
    return this.http.post<any>(this.toolUrl + '/configs', config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putCampaignConfiguration(config: AROrgeCampaignConfiguration) {
    const url = `${this.toolUrl}/configs/${config.id}`;

    return this.http.put<any>(url, config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private handleCampaignDates(campaign: AROrgeCampaign): AROrgeCampaign {
    campaign.startingOn = new Date(<string>campaign.startingOn);
    campaign.endingOn = new Date(<string>campaign.endingOn);
    campaign.createdAt = new Date(<string>campaign.createdAt);
    campaign.updatedAt = new Date(<string>campaign.updatedAt);

    return campaign;
  }

  private handleConfigurationDates(config: AROrgeCampaignConfiguration): AROrgeCampaignConfiguration {
    if (config.startingOn) {
      config.startingOn = new Date(<string>config.startingOn);
    }
    if (config.orderT1EndingOn) {
      config.orderT1EndingOn = new Date(<string>config.orderT1EndingOn);
    }
    if (config.dataT1EndingOn) {
      config.dataT1EndingOn = new Date(<string>config.dataT1EndingOn);
    }
    if (config.orderT2EndingOn) {
      config.orderT2EndingOn = new Date(<string>config.orderT2EndingOn);
    }
    if (config.dataT2EndingOn) {
      config.dataT2EndingOn = new Date(<string>config.dataT2EndingOn);
    }
    if (config.orderT3EndingOn) {
      config.orderT3EndingOn = new Date(<string>config.orderT3EndingOn);
    }
    if (config.dataT3EndingOn) {
      config.dataT3EndingOn = new Date(<string>config.dataT3EndingOn);
    }

    config.createdAt = new Date(<string>config.createdAt);
    config.updatedAt = new Date(<string>config.updatedAt);

    return config;
  }

  private handleOrderDates(order: AROrgeOrder): AROrgeOrder {
    order.createdAt = new Date(<string>order.createdAt);
    order.updatedAt = new Date(<string>order.updatedAt);

    return order;
  }

  getPageParcels(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: AROrgeParcelFilter,
  ): Observable<Page<AROrgeParcel>> {
    let url = this.toolUrl + '/parcels?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    url += this.buildParamsFromFilter(filter);
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        data.content.forEach((element: AROrgeParcel) => {
          this.handleParcel(element);
          if (element.campaign) {
            this.handleCampaignDates(<AROrgeCampaign>element.campaign);
          }
          if ((<AROrgeCampaign>element.campaign).configuration) {
            this.handleConfigurationDates((<AROrgeCampaign>element.campaign).configuration);
          }
        });

        return data;
      }),
      catchError(this.handleError),
    );
  }

  getAllParcelIds(filter: AROrgeParcelFilter): Observable<number[]> {
    let url = this.toolUrl + '/parcels/all?onlyIds=true';
    url += this.buildParamsFromFilter(filter);
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        return data.map((x: AROrgeParcel) => x.id);
      }),
      catchError(this.handleError),
    );
  }

  buildParamsFromFilter(filter: AROrgeParcelFilter): string {
    let url = '';
    if (filter) {
      if (filter.prefixParcel) {
        url += '&prefixParcel=' + filter.prefixParcel;
      }
      if (filter.prefixExploitation) {
        url += '&prefixExploitation=' + filter.prefixExploitation;
      }
      if (filter.prefixIdParcel) {
        url += '&prefixIdParcel=' + filter.prefixIdParcel;
      }
      if (filter.farmId) {
        url += '&farm=' + filter.farmId;
      }
      if (filter.organisationId) {
        url += '&organisation=' + filter.organisationId;
      }
      if (filter.campaignId) {
        if (typeof filter.campaignId === 'number' || typeof filter.campaignId === 'string') {
          url += '&campaign=' + filter.campaignId;
        } else {
          url += '&campaign=' + (<number[]>filter.campaignId).join(',');
        }
      }
      if (filter.orderStatus === true || filter.orderStatus === false) {
        url += '&orderStatus=' + filter.orderStatus;
      }
      if (filter.completionStatus === true || filter.completionStatus === false) {
        url += '&completionStatus=' + filter.completionStatus;
      }
      if (filter.specy) {
        url += '&specy=' + filter.specy;
      }
      if (filter.advisorId) {
        url += '&advisor=' + filter.advisorId;
      }
    }
    return url;
  }

  handleParcel(parcel: AROrgeParcel): AROrgeParcel {
    if (parcel.sowedOn) {
      parcel.sowedOn = new Date(<string>parcel.sowedOn);
    }
    parcel.createdAt = new Date(<string>parcel.createdAt);
    parcel.updatedAt = new Date(<string>parcel.updatedAt);
    return parcel;
  }

  getParcel(id: number): Observable<AROrgeParcel> {
    return this.http.get<any>(this.toolUrl + '/parcels/' + id).pipe(
      map((response) => {
        const data: AROrgeParcel = response.data;
        this.handleParcel(data);
        if (data.campaign) {
          this.handleCampaignDates(<AROrgeCampaign>data.campaign);
        }
        if ((<AROrgeCampaign>data.campaign).configuration) {
          this.handleConfigurationDates((<AROrgeCampaign>data.campaign).configuration);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  // FIXME utiliser seulement orderableStatus sur le serveur au lieu de conditions custom
  getOrderItems(
    orderItemIds: Array<number>,
    completionStatus: boolean,
    orderStatus: boolean,
    orderableStatus: boolean = null,
  ): Observable<AROrgeOrderItem[]> {
    let url = this.toolUrl + '/orderitems/all';
    url += this.fillOrderItemFilter(orderItemIds, completionStatus, orderStatus);

    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: AROrgeOrderItem[] = response.data;
        return data;
      }),
      catchError(this.handleError),
    );
  }

  countOrderItems(
    completionStatus: boolean,
    orderStatus: boolean,
    orderableStatus: boolean = null,
  ): Observable<number> {
    let url = this.toolUrl + '/orderitems/count';
    url += this.fillOrderItemFilter(null, completionStatus, orderStatus);

    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: number = response.data;
        return data;
      }),
      catchError(this.handleError),
    );
  }

  // FIXME commun à tous les services agrorendement
  fillOrderItemFilter(orderItemIds: Array<number>, completionStatus: boolean, orderStatus: boolean) {
    let url = '';
    if (orderItemIds !== null || completionStatus !== null || orderStatus !== null) {
      let q = '';
      if (completionStatus !== null) {
        q += 'completionStatus=' + completionStatus;
      }
      if (orderStatus !== null) {
        if (q.length > 0) {
          q += '&';
        }
        q += 'orderStatus=' + orderStatus;
      }
      if (orderItemIds !== null) {
        if (q.length > 0) {
          q += '&';
        }
        q += 'orderItemIds=' + orderItemIds.join(',');
      }
      url += '?' + q;
    }

    return url;
  }

  getOrderItem(id: number): Observable<AROrgeOrderItem> {
    return this.http.get<any>(this.toolUrl + '/orderitems/' + id).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  getOrderItemByParcel(parcel: Parcel): Observable<AROrgeOrderItem> {
    return this.http.get<any>(this.toolUrl + '/orderitems/parcel/' + parcel.id).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  saveOrderItem(orderItem: AROrgeOrderItem): Observable<AROrgeOrderItem> {
    if (orderItem.id) {
      return this.putOrderItem(orderItem);
    }
    return this.postOrderItem(orderItem);
  }

  deleteOrderItem(orderItem: AROrgeOrderItem) {
    const url = `${this.toolUrl}/orderitems/${orderItem.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  deleteParcelOrderItem(parcel: Parcel) {
    const url = `${this.toolUrl}/orderitems/parcel/${parcel.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  deleteParcelOrderItems(parcelIds: number[]): Observable<OrderItemDeletionSummary> {
    const url = `${this.toolUrl}/orderitems/parcel/delete-batch`;
    const body = {
      parcelIds: parcelIds,
    };

    return this.http.post<any>(url, body).pipe(
      map((response) => {
        const parsed = tryJson(response);
        if (parsed && parsed.data) {
          return normalizeDeletionSummary(parsed.data);
        }
        return normalizeDeletionSummary(parsed);
      }),
      catchError(this.handleError),
    );
  }

  // Add new orderItem
  private postOrderItem(config: AROrgeOrderItem): Observable<AROrgeOrderItem> {
    return this.http.post<any>(this.toolUrl + '/orderitems', config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putOrderItem(orderItem: AROrgeOrderItem): Observable<AROrgeOrderItem> {
    const url = `${this.toolUrl}/orderitems/${orderItem.id}`;

    return this.http.put<any>(url, orderItem).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  getPageOrders(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: AROrderFilter,
  ): Observable<Page<AROrgeOrder>> {
    let url = this.toolUrl + '/orders?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    url += fillOrderFilter(filter);
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: Page<AROrgeOrder> = response.data;
        for (const order of data.content) {
          this.handleOrderDates(order);
          if (order.campaign) {
            this.handleCampaignDates(order.campaign);
            if (order.campaign.configuration) {
              this.handleConfigurationDates(order.campaign.configuration);
            }
          }
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getOrder(id: number): Observable<AROrgeOrder> {
    return this.http.get<any>(this.toolUrl + '/orders/' + id).pipe(
      map((response) => {
        const data: AROrgeOrder = response.data;
        this.handleOrderDates(data);
        if (data.campaign) {
          this.handleCampaignDates(data.campaign);
          if (data.campaign.configuration) {
            this.handleConfigurationDates(data.campaign.configuration);
          }
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  saveOrder(order: AROrgeOrder): Observable<AROrgeOrder> {
    if (order.id) {
      return this.putOrder(order);
    }
    return this.postOrder(order);
  }

  deleteOrder(order: AROrgeOrder) {
    const url = `${this.toolUrl}/orders/${order.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  getAreaForOrders(filter: AROrderFilter, specy: string): Observable<OrderSummary> {
    let url = `${this.toolUrl}/orders/area?`;
    url += fillOrderFilter(filter);
    if (specy != null) {
      url += `&specy=${specy}`;
    }
    return this.http.get<any>(url).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  exportKml(): Observable<Blob> {
    const url = this.toolUrl + '/kml';
    const applicationType = 'application/zip';

    return this.httpWanaka.blobQuery(url, applicationType);
  }

  // Add new orderItem
  private postOrder(order: AROrgeOrder): Observable<AROrgeOrder> {
    return this.http.post<any>(this.toolUrl + '/orders', order).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putOrder(order: AROrgeOrder): Observable<AROrgeOrder> {
    const url = `${this.toolUrl}/orders/${order.id}`;

    return this.http.put<any>(url, order).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }
}
